import { useEffect, useMemo, useState } from 'react';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import BaseMultiAutocomplete from '@/components/shared/Autocomplete/BaseMultiAutocomplete';
import { BaseMultiAutocompleteProps } from '@/components/shared/Autocomplete/types';
import { useBudgetGroupsAutocomplete } from '@/hooks/autocomplete/useBudgetGroupsAutocomplete';
import { EnhancedBudgetGroup } from '@/hooks/useManageUserBudgetGroups';
import { useUserBudgetGroupMembership } from '@/hooks/useUserBudgetGroupMembership';

export interface BudgetGroupAutocompleteForUserProps<BudgetGroup> {
  userId: string;
  organizationId: number;
  value: EnhancedBudgetGroup[];
  onChange: BaseMultiAutocompleteProps<BudgetGroup>['autocomplete']['onChange'];
  onApply?: NonNullable<BaseMultiAutocompleteProps<BudgetGroup>['actionButton']>['onApply'];
  disabled?: boolean;
  buttonDisabled?: boolean;
}

function BudgetGroupAutocompleteForUser(props: BudgetGroupAutocompleteForUserProps<EnhancedBudgetGroup>) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: groups, isLoading: isLoadingGroups } = useBudgetGroupsAutocomplete({
    organizationId: props.organizationId,
    filter: debouncedValue,
    enabled: !props.disabled,
  });

  const groupIds = useMemo(() => groups?.map((g) => g.id).filter((id): id is number => id !== undefined) || [], [groups]);

  const {
    data: membershipData,
    isLoading: isLoadingMembership,
    isRefetching: isRefetchingMembership,
  } = useUserBudgetGroupMembership({
    userId: props.userId,
    organizationId: props.organizationId,
    groupIds,
    enabled: !props.disabled && groupIds.length > 0,
  });

  const enhancedOptions = useMemo<EnhancedBudgetGroup[]>(() => {
    if (!groups) return [];
    if (isLoadingMembership || isRefetchingMembership) return [];
    if (!membershipData)
      return groups.map((group) => ({
        ...group,
        isAssignedToUser: false,
        role: undefined,
      }));

    const membershipMap = new Map(membershipData.map((m) => [m.groupId, m]));

    return groups.map((group) => {
      const membership = membershipMap.get(group.id);
      return {
        ...group,
        isAssignedToUser: membership?.isMember || false,
        role: membership?.role ?? undefined,
      };
    });
  }, [groups, membershipData, isLoadingMembership, isRefetchingMembership]);

  const handleInputChange = (_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  };

  const getOptionLabel = (option: EnhancedBudgetGroup) => option.name || '';

  const getOptionDisabled = (optionLabel: EnhancedBudgetGroup) => {
    if (isLoadingMembership || !membershipData) {
      return false;
    }
    const optionToDisable = enhancedOptions.find((g) => g.name === optionLabel.name);

    const isSelectedOptionDisabled = props.value.some((val) => val.name === optionLabel.name);

    return optionToDisable?.isAssignedToUser || isSelectedOptionDisabled;
  };

  return (
    <BaseMultiAutocomplete<EnhancedBudgetGroup>
      autocomplete={{
        options: enhancedOptions,
        value: props.value,
        inputValue: inputValue,
        loading: isLoadingGroups || isLoadingMembership || isRefetchingMembership,
        onChange: props.onChange,
        onInputChange: handleInputChange,
        getOptionLabel: getOptionLabel,
        getOptionDisabled: getOptionDisabled,
        label: 'Search Group to Add',
        disabled: props.disabled,
      }}
      actionButton={{
        buttonLabel: 'Add',
        onApply: props.onApply,
      }}
    />
  );
}

export default BudgetGroupAutocompleteForUser;
